/*
 * <xali-key-features> — Xali custom element for the Key Features Content section.
 *
 * Selecting a feature reveals its supporting copy and, when the section is set
 * to per-feature media, cross-fades the matching media panel. The compare
 * slider variant renders no panels, so selection only affects the text column.
 *
 * Under the "Timed rule" indicator the component also advances on its own,
 * driven by the CSS fill animation ending rather than a timer here. It pauses
 * while hovered, focused or scrolled out of view, and stops for good the moment
 * the customer picks a feature — that is the WCAG 2.2.2 stop mechanism, and it
 * keeps the section from pulling content away from someone mid-read.
 *
 * Transitions live in xali-key-features.css; this only moves the is-active
 * class and the matching ARIA state.
 */
!(function () {
  "use strict";

  if (customElements.get("xali-key-features")) return;

  customElements.define(
    "xali-key-features",
    class extends HTMLElement {
      constructor() {
        super();
        this.activeIndex = -1;
        this.timed = false;
        this.hovered = false;
        this.visible = true;
        this.observer = null;

        this.onClick = this.onClick.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onAnimationEnd = this.onAnimationEnd.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onResume = this.onResume.bind(this);
      }

      connectedCallback() {
        this.items = Array.from(this.querySelectorAll("[data-feature-item]"));
        this.panels = Array.from(this.querySelectorAll("[data-media-panel]"));
        this.list = this.querySelector("[data-indicator]");
        if (this.items.length === 0) return;

        this.addEventListener("click", this.onClick);
        document.addEventListener("shopify:block:select", this.onBlockSelect);

        if (
          this.list &&
          this.list.dataset.indicator === "timed" &&
          !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
          this.startTimed();
        }

        const initial = this.items.findIndex((item) => item.classList.contains("is-active"));
        this.select(initial === -1 ? 0 : initial);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.onClick);
        document.removeEventListener("shopify:block:select", this.onBlockSelect);
        this.stopTimed();
      }

      startTimed() {
        this.timed = true;
        this.addEventListener("animationend", this.onAnimationEnd);
        this.addEventListener("pointerenter", this.onPause);
        this.addEventListener("pointerleave", this.onResume);
        this.addEventListener("focusin", this.onPause);
        this.addEventListener("focusout", this.onResume);

        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              this.visible = entries[0].isIntersecting;
              this.syncPaused();
            },
            { threshold: 0.2 }
          );
          this.observer.observe(this);
        }
      }

      stopTimed() {
        if (!this.timed) return;
        this.timed = false;

        this.removeEventListener("animationend", this.onAnimationEnd);
        this.removeEventListener("pointerenter", this.onPause);
        this.removeEventListener("pointerleave", this.onResume);
        this.removeEventListener("focusin", this.onPause);
        this.removeEventListener("focusout", this.onResume);

        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        this.list.classList.remove("is-paused");
        this.list.classList.add("is-stopped");
      }

      syncPaused() {
        if (!this.timed) return;

        this.list.classList.toggle("is-paused", this.hovered || !this.visible);
      }

      onPause() {
        this.hovered = true;
        this.syncPaused();
      }

      onResume(event) {
        // focusout also fires moving between two triggers in the same list.
        if (event.type === "focusout" && this.contains(event.relatedTarget)) return;

        this.hovered = false;
        this.syncPaused();
      }

      onAnimationEnd(event) {
        if (event.animationName !== "kf-fill") return;

        this.select((this.activeIndex + 1) % this.items.length);
      }

      onClick(event) {
        const trigger = event.target.closest("[data-feature-trigger]");
        if (!trigger) return;

        this.select(this.items.indexOf(trigger.closest("[data-feature-item]")));
        this.stopTimed();
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;

        const item = event.target.closest("[data-feature-item]");
        if (!item) return;

        this.select(this.items.indexOf(item));
        this.stopTimed();
      }

      select(index) {
        if (index < 0 || index === this.activeIndex) return;
        this.activeIndex = index;

        this.items.forEach((item, i) => {
          const isActive = i === index;
          item.classList.toggle("is-active", isActive);

          const trigger = item.querySelector("[data-feature-trigger]");
          if (trigger && trigger.hasAttribute("aria-expanded")) {
            trigger.setAttribute("aria-expanded", isActive);
          }
        });

        this.panels.forEach((panel, i) => {
          panel.classList.toggle("is-active", i === index);
          panel.setAttribute("aria-hidden", i !== index);
        });
      }
    }
  );
})();
